import { useState, useEffect } from 'react'
import { useAppStore } from '../../store/app'

import { createChatRoom } from '../../api/ws'
import { wsRequest } from '../../api/ws'
import '../agents/NewAgentWizard.css'

type Step = 'name' | 'host' | 'members' | 'creating'

export function CreateChatRoomSheet() {
  const popSheet = useAppStore((s) => s.popSheet)
  const hosts = useAppStore((s) => s.hosts)
  const organizationID = useAppStore((s) => s.organizationID)
  const organizationMembers = useAppStore((s) => s.organizationMembers)
  const humanUserID = useAppStore((s) => s.humanUserID)

  const [step, setStep] = useState<Step>('name')
  const [name, setName] = useState('')
  const [hostID, setHostID] = useState('')
  const [selectedMemberIDs, setSelectedMemberIDs] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  // Auto-select the single host if there's only one.
  useEffect(() => {
    if (hosts.length === 1) setHostID(hosts[0].host_id)
  }, [hosts])

  // Fetch org members when we reach the members step.
  useEffect(() => {
    if (step !== 'members' || !organizationID) return
    wsRequest('list_organization_members', { organization_id: organizationID }, 'organization_members', {
      kind: 'organization',
      id: organizationID,
      action: 'list_members',
    }).catch(() => {})
  }, [step, organizationID])

  function back() {
    if (step === 'name') { popSheet(); return }
    if (step === 'host') { setStep('name'); return }
    if (step === 'members') { setStep(hosts.length > 1 ? 'host' : 'name'); return }
  }

  function nextFromName() {
    if (!name.trim()) { setError('Name is required'); return }
    setError(null)
    if (hosts.length > 1) {
      setStep('host')
    } else {
      setStep('members')
    }
  }

  function nextFromHost() {
    if (!hostID) { setError('Select a host'); return }
    setError(null)
    setStep('members')
  }

  async function submit() {
    setStep('creating')
    setError(null)
    try {
      const r = await createChatRoom({
        orgID: organizationID ?? '',
        name: name.trim(),
        hostID: hostID || undefined,
        memberUserIDs: selectedMemberIDs,
      })
      if (r.error) {
        setError(r.error)
        setStep('name')
        return
      }
      popSheet()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create room')
      setStep('name')
    }
  }

  const titleFor: Record<Step, string> = {
    name: 'New Chat Room',
    host: 'Choose Host',
    members: 'Add Members',
    creating: 'Creating…',
  }

  return (
    <div className="wizard wizard--sheet">
      <header className="wizard__nav">
        <button className="wizard__nav-btn" onClick={back}>
          {step === 'name' ? 'Cancel' : '‹ Back'}
        </button>
        <div className="wizard__nav-title">{titleFor[step]}</div>
        <div className="wizard__nav-spacer" />
      </header>

      <div className="wizard__body">
        {error && <div className="wizard__error">{error}</div>}

        {step === 'name' && (
          <div className="wizard__step">
            <div className="wizard__hint">Give this room a short, memorable name.</div>
            <input
              className="wizard__input"
              placeholder="e.g. design, backend, general"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') nextFromName() }}
              autoFocus
            />
            <button
              className="wizard__primary"
              onClick={nextFromName}
              disabled={!name.trim()}
            >
              Next
            </button>
          </div>
        )}

        {step === 'host' && (
          <div className="wizard__step">
            <div className="wizard__hint">Which host should files in this room land on?</div>
            <ul className="wizard__list">
              {hosts.map((h) => (
                <li key={h.host_id}>
                  <button
                    className={`wizard__row${h.host_id === hostID ? ' wizard__row--selected' : ''}`}
                    onClick={() => setHostID(h.host_id)}
                  >
                    <span>{h.hostname || h.host_id}</span>
                    <span
                      className="wizard__dot"
                      style={{ background: h.online ? 'var(--success)' : 'var(--label-tertiary)' }}
                      aria-label={h.online ? 'online' : 'offline'}
                    />
                  </button>
                </li>
              ))}
            </ul>
            <button
              className="wizard__primary"
              onClick={nextFromHost}
              disabled={!hostID}
            >
              Next
            </button>
          </div>
        )}

        {step === 'members' && (
          <div className="wizard__step">
            <div className="wizard__hint">
              Who should start in this room? Everyone in your org can join later.
            </div>
            <ul className="wizard__list">
              {organizationMembers
                .filter((m) => m.human_user_id !== humanUserID)
                .map((m) => {
                  const checked = selectedMemberIDs.includes(m.human_user_id)
                  return (
                    <li key={m.human_user_id}>
                      <button
                        className={`wizard__row${checked ? ' wizard__row--selected' : ''}`}
                        onClick={() =>
                          setSelectedMemberIDs((prev) =>
                            checked
                              ? prev.filter((id) => id !== m.human_user_id)
                              : [...prev, m.human_user_id],
                          )
                        }
                      >
                        <span>{m.name || m.email || m.human_user_id}</span>
                        {checked && <span style={{ color: 'var(--accent)' }}>✓</span>}
                      </button>
                    </li>
                  )
                })}
            </ul>
            <button className="wizard__primary" onClick={submit}>
              {selectedMemberIDs.length > 0 ? `Create with ${selectedMemberIDs.length + 1} members` : 'Create Room'}
            </button>
          </div>
        )}

        {step === 'creating' && (
          <div className="wizard__step">
            <div className="wizard__hint" style={{ textAlign: 'center', paddingTop: 48 }}>⏳ Creating room…</div>
          </div>
        )}
      </div>
    </div>
  )
}
